import { useMemo, Fragment, ReactNode } from 'react';

import { FormFieldType } from 'types';
import { isGradedSelect } from 'utils/choice-response';

import { FormHeading } from './heading';
import { FormInputField } from './input';
import { FormRadioField } from './radio';
import { FormSelectField } from './select';
import { FormCheckboxField } from './checkbox';
import { FieldArrayField } from './field-array';
import { DynamicFormFieldsProps } from './types';
import { FormScaleRatingField } from './scale-rating';
import { FormSelectCorrectField } from './select-correct';

export const DynamicFormFields = ({
  fields,
  isSaved,
  container,
  hideLabels,
}: DynamicFormFieldsProps) => {
  const items = useMemo(() => {
    // Headings bypass container callbacks, so wrapped fields keep contiguous indexes.
    let wrappedFieldIndex = 0;
    return fields.map((field, index) => {
      let fieldNode: ReactNode;
      const props = { ...field };
      if (hideLabels && props.type !== FormFieldType.HEADING) {
        props.label = '';
      }
      switch (props.type) {
        case FormFieldType.INPUT:
          fieldNode = <FormInputField key={props.id} {...props} />;
          break;
        case FormFieldType.SELECT:
          fieldNode = isGradedSelect(props) ? (
            <FormSelectCorrectField
              key={props.id}
              {...props}
              disabled={isSaved ? isSaved(props.id) : false}
            />
          ) : (
            <FormSelectField key={props.id} {...props} />
          );
          break;
        case FormFieldType.RADIO:
          fieldNode = <FormRadioField key={props.id} {...props} />;
          break;
        case FormFieldType.CHECKBOX:
          fieldNode = <FormCheckboxField key={props.id} {...props} />;
          break;
        case FormFieldType.SCALE_RATING:
          fieldNode = <FormScaleRatingField key={props.id} {...props} />;
          break;
        case FormFieldType.FIELD_ARRAY:
          fieldNode = <FieldArrayField key={props.id} {...props} />;
          break;
        case FormFieldType.HEADING:
          fieldNode = <FormHeading key={props.id} {...props} />;
          break;
        default:
          fieldNode = <Fragment key={index} />;
          break;
      }
      if (field.type === FormFieldType.HEADING) {
        return fieldNode;
      }

      const containerIndex = wrappedFieldIndex;
      wrappedFieldIndex += 1;
      return container
        ? container(field, fieldNode, containerIndex)
        : fieldNode;
    });
  }, [fields, container, isSaved, hideLabels]);

  return <>{items}</>;
};
